import {
  FluidButtonConfig,
  FluidFormElementConfig,
  FluidFormElementState,
  FluidFormElementType,
  FluidIconConfig,
  FluidIconKey,
  FluidPopoverConfig,
  FluidSearchOptions,
  FluidSearchType,
  FluidStateStrategy,
  FluidTheme,
  FluidThemeInterface,
  generateUniqueId,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Method,
  Prop,
  State,
} from '@stencil/core';

import { SEARCH_BOX_HEADER_TYPE_WARNING } from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { FluidBrowserStorage } from '../../utils/store/local-storage.class';
import { themeVar } from '../../utils/themes/theme.variables';
import { Utils } from '../../utils/utils';
import {
  FluidSearchBoxTranslationOptions,
  SearchBox,
  SearchButton,
} from './components/fluid-search-box.components';

/**
 * @displayName Search Box
 *
 */
@Component({
  tag: 'fluid-search-box',
  styleUrl: 'fluid-search-box.scss',
  shadow: true,
})
export class FluidSearchBox implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  private _searchHistoryLimit = 10; // Default value

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidSearchBoxElement;

  // ================================================================ //
  // -- States

  /**
   * Boolean that tracks whether Search Box currently has :focus.
   */
  @State() _hasFocus = false;

  @State() _recentSearches: any[];

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Configures the search options.
   */
  @Prop({ mutable: true }) searchOptions: FluidSearchOptions = {
    searchType: FluidSearchType.DEFAULT,
    dynamicSearch: false,
    minCharacters: -1,
  };

  /**
   * Allows the search term to be pre-populated (note: this is a mutable property
   * so will change when the user types in the search box)
   */
  @Prop({ mutable: true }) searchTerm: string;

  /**
   * Determines whether the search box should enable search history functionality.
   * When set to `true`, the component will store and display a history of previous searches.
   */
  @Prop({ mutable: true }) enableSearchHistory = false;

  /**
   * The maximum number of recent searches to display.
   */
  @Prop({ mutable: true }) searchHistoryLimit = 5;

  // ================================================================ //
  // -- Events

  /**
   * Emits when the search button has been clicked
   */
  @Event() search: EventEmitter;

  /**
   * Emits when the search term is updated
   */
  @Event() searchTermUpdated: EventEmitter;

  /**
   * Emits when the search box is cleared, or if the search term is deleted.
   */
  @Event() searchCleared: EventEmitter;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the saerch-box in order
   * to help facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.enforceSearchHistoryLimit(this.searchHistoryLimit);
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Listener for :focus events WITHIN Search Box that sets internal :focus state
   * of component. Triggered when any descendant of Search Box host (e.g. text input,
   * buttons, etc.) receives :focus. This is distinct from a :focus listener in that
   * it's only triggered when a descendant FIRST receives :focus, and then is NOT
   * triggered again when :focus changes between descendant elements.
   */
  @Listen('focusin')
  onFocus() {
    this._hasFocus = true;
  }

  /**
   * Listener for blur events WITHIN Search Box. Triggered when ALL Search
   * Box descendants lose :focus. This is distinct from a blur listener in
   * that it isn't triggered on INTERNAL blur events fired when :focus changes
   * between descendants.
   */
  @Listen('focusout')
  onBlur() {
    this._hasFocus = false;
  }

  @Method() async getSearchTerm(): Promise<string> {
    return Promise.resolve(this.searchTerm);
  }

  // ================================================================ //
  // -- Local Methods

  getHelpText(searchBox: Partial<FluidFormElementConfig>, defaultText: string) {
    if (!searchBox) {
      return (
        this._i18nStore?.translate(
          this.fluidTranslate?.searchBox?.helpText as string
        ) || defaultText
      );
    }

    const { helpText } = searchBox;

    if (typeof helpText === 'string') {
      return (
        this._i18nStore?.translate(
          this.fluidTranslate?.searchBox?.helpText as string
        ) || helpText
      );
    }

    if (typeof helpText === 'object') {
      return helpText as FluidPopoverConfig;
    }
  }

  // only for dynamic search
  getAdornmentConfig(searchType: FluidSearchType): FluidIconConfig {
    if (!this.searchOptions?.dynamicSearch) return;

    if (searchType && searchType.toLowerCase() !== FluidSearchType.DEFAULT) {
      return {
        key: FluidIconKey.SEARCH,
        ...themeVar<FluidIconConfig>(this.theme, 'icons.search_box.adornment'),
      };
    } else {
      return null;
    }
  }

  handleSearchBoxValueChange(
    state: FluidFormElementState,
    selectRef: HTMLFluidSelectElement
  ) {
    const { value, cleared } = state;

    // Update the internal searchTerm state.
    this.searchTerm = value;
    this.searchTermUpdated.emit(value);
    Utils.debug('[SEARCH] Search term updated in search box', value);

    // If the search box has been cleared, reset the select element if it exists
    if (cleared && selectRef?.reset) {
      selectRef.reset().then();
      Utils.debug('[SEARCH] Search select cleared');
    }

    // If the search box has been cleared or, the value is now empty, and a search has previously been applied, emit the searchCleared event.
    if (cleared || (value === '' && this._searchApplied)) {
      this.searchCleared.emit();
      Utils.debug('[SEARCH] Search cleared');
    }
  }

  getSearchBoxType(): string {
    if (this.searchOptions?.dynamicSearch) {
      switch (
        this.searchOptions.searchType &&
        this.searchOptions.searchType.toLowerCase()
      ) {
        case FluidSearchType.HEADER:
          if (Utils.closestAncestor(this.host, 'fluid-header')) {
            return 'header-search-box'; // has styles that only work in non-white backgrounds
          } else {
            Utils.consoleWarn(this.host, SEARCH_BOX_HEADER_TYPE_WARNING);
            return 'pill-search-box'; // if requesting header outside of the header, return the pill
          }
        case FluidSearchType.PILL:
          return 'pill-search-box'; // styles work with white background
        default:
          return 'default-search-box';
      }
    } else {
      return 'default-search-box';
    }
  }

  // Method to check if searchTerm matches any labels in recentSearchList
  searchTermMatchesRecentSearch(
    searchTerm: string,
    recentSearchList: any[]
  ): boolean {
    return recentSearchList.some((searchItem) =>
      searchItem.links.some((link) => link.label === searchTerm)
    );
  }

  enforceSearchHistoryLimit(limit: number): void {
    if (limit > this._searchHistoryLimit) {
      Utils.consoleWarn(
        this.host,
        `Search history limit is set to ${limit}, but the maximum allowed is ${this._searchHistoryLimit}.`
      );
      this.searchHistoryLimit = this._searchHistoryLimit; // Set to a default value
    }
    if (this.enableSearchHistory) {
      this._recentSearches = this.getRecentSearches(this.searchHistoryLimit);
    }
  }

  getRecentSearches(limit: number) {
    const links = Array.from({ length: limit }, (_, i) => {
      const searchLabel = FluidBrowserStorage.get(
        `searchId-${i}`,
        FluidStateStrategy.LOCAL
      );
      return searchLabel ? { id: `searchId-${i}`, label: searchLabel } : null;
    }).filter(Boolean); // Remove null values

    return [
      {
        type: 'link',
        links,
      },
    ];
  }

  onSearch() {
    if (this.enableSearchHistory) {
      const recentSearchesList = this.getRecentSearches(
        this.searchHistoryLimit
      );

      if (
        !this.searchTermMatchesRecentSearch(
          this.searchTerm,
          recentSearchesList
        ) &&
        this.searchTerm?.trim()?.length > 0
      ) {
        // Shift existing entries up by one position
        this.shiftRecentSearches();
        // Add the new search term to the first position
        FluidBrowserStorage.set(
          'searchId-0',
          this.searchTerm,
          FluidStateStrategy.LOCAL
        );
      }
      this._recentSearches = this.getRecentSearches(this.searchHistoryLimit);
    }
    this.search.emit();
  }

  // Helper method to shift recent searches up by one position
  private shiftRecentSearches() {
    for (let i = this.searchHistoryLimit - 1; i > 0; i--) {
      const previousLabel = FluidBrowserStorage.get(
        `searchId-${i - 1}`,
        FluidStateStrategy.LOCAL
      );
      // Only shift if the previous label is not null
      if (previousLabel !== null) {
        FluidBrowserStorage.set(
          `searchId-${i}`,
          previousLabel,
          FluidStateStrategy.LOCAL
        );
      }
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const searchConfig = {
      ...this.searchOptions?.searchBox,
      inlineField: true,
      controlName: 'search',
      allowCancel: true,
      initialValue: this.searchTerm,
      elementType: FluidFormElementType.INPUT,
      placeholder:
        this._i18nStore?.translate(
          this.fluidTranslate?.searchBox?.placeholder
        ) || this.searchOptions?.searchBox?.placeholder,
      theme: this.theme,
      overrideGlobalTheme: true,
      identifier: this.identifier,
      adornmentConfig: this.getAdornmentConfig(this.searchOptions?.searchType),
    } as FluidFormElementConfig;

    const buttonConfig = {
      ...this.searchOptions?.searchButton,
      label:
        this._i18nStore?.translate(this.fluidTranslate?.searchButton?.label) ||
        this.searchOptions?.searchButton?.label ||
        'Search',
      disabled: !this.searchTerm,
      alignedWithFormElement: false,
      removeMargin: true,
    };

    return (
      <div
        class={`fluid-search-box theme-${this.theme}`}
        data-test-id={`${this.identifier}-search-box`}>
        <SearchBox
          onSearchBoxValueChange={this.handleSearchBoxValueChange.bind(this)}
          onSearch={() => {
            this.onSearch();
          }}
          deleteDebounceTime={this.searchOptions?.deleteDebounceTime}
          searchTerm={this.searchTerm}
          searchConfig={searchConfig}
          helpText={this.getHelpText(
            this.searchOptions?.searchBox,
            this.searchOptions?.helpText
          )}
          identifier={this.identifier}
          searchBoxType={this.getSearchBoxType()}
          selectConfig={this.searchOptions?.searchSelectOptions}
          hasFocus={this._hasFocus}
          enableSearchHistory={this.enableSearchHistory}
          recentSearches={this._recentSearches}
        />
        <SearchButton
          dynamicSearch={this.searchOptions?.dynamicSearch}
          offsetLabel={!!searchConfig.label}
          onSearch={() => {
            this.onSearch();
          }}
          buttonConfig={buttonConfig as FluidButtonConfig}
          theme={this.theme}
          identifier={this.identifier}
        />
      </div>
    );
  }

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidSearchBoxTranslationOptions;
  private _translateId: string;
  @State() private _i18nStore: FluidI18nStore | undefined;

  /**
   * @internal
   * Lets the search box know if a search is already applied.
   */
  @Prop({ mutable: true }) _searchApplied: boolean;
}
